import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Table from '@/components/atoms/Table/Table';
import {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { selectBroadcastId, selectAccountSlug } from '@/redux/session/selectors';
import { Subscriber } from '@/shared/types/broadcast';

import BroadcastLayout from '../components/BroadcastLayout/BroadcastLayout';
import { SidebarBox } from '../components/BroadcastSidebar/SidebarBox/SidebarBox';
import SidebarFields from '../components/BroadcastSidebar/SidebarFields/SidebarFields';
import { useBroadcasts } from '../hooks/useBroadcasts';
import { useSubscribers } from '../hooks/useSubscribers';
import {
  selectDraftSubscribers,
  selectErrorCounter,
  selectIsDraft,
  selectValidSubscribers,
} from '../redux/selectors';
import { flattenKeys, getContextVariableOptions } from '../utils';

interface RowProps {
  row: { original: Partial<Subscriber> };
}
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const BroadcastAudience = () => {
  const loadMoreRef = useRef(null);
  const oldScrollRef = useRef<number>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const draftSubscribers = useSelector(selectDraftSubscribers);
  const validSubscribers = useSelector(selectValidSubscribers);
  const isDraft = useSelector(selectIsDraft);
  const errorCounter = useSelector(selectErrorCounter);

  const broadcastId = useSelector(selectBroadcastId);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleBackClick = useCallback(() => {
    if (isDraft) {
      navigate(`/${slug}/broadcasts/draft`);
    } else {
      navigate(`/${slug}/broadcasts/${broadcastId}`);
    }
  }, [broadcastId, isDraft, navigate, slug]);

  const { broadcast: APIbroadcast } = useBroadcasts({ broadcastId });

  const { isLoading, subscribers, fetchNextPage, hasNextPage } = useSubscribers(broadcastId);

  const flatSubscribers = useMemo(
    () => [...(subscribers?.pages ?? [])].flatMap((page) => page.subscribers),
    [subscribers?.pages]
  );
  const finalSubscribers = useMemo(
    () => [...draftSubscribers, ...flatSubscribers],
    [draftSubscribers, flatSubscribers]
  );

  const handleNextPageFetch = useCallback(() => {
    oldScrollRef.current = bodyRef.current.scrollHeight;
    return fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    if (bodyRef.current && !isLoading) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [isLoading]);

  useIntersectionObserver({ target: loadMoreRef, onIntersect: handleNextPageFetch });

  const getColumns = useMemo(() => {
    const cols: {
      Header: string | React.JSX.Element;
      Cell: (data) => React.JSX.Element;
      accessor?: string;
      id?: string;
    }[] = [
      {
        Header: 'external_id',
        accessor: 'external_id',
        Cell: (props: RowProps) => {
          const external_id = props.row.original?.external_id;
          if (!external_id) {
            return (
              <div className="flex h-full w-full items-center">
                <Badge type="secondary" flavour="warning">
                  {t('broadcasts.missing')}
                </Badge>
              </div>
            );
          }
          return <>{external_id}</>;
        },
      },
    ];

    const firstSubscriberContext = finalSubscribers?.[0]?.context ?? {};
    const flattenedContextKeys = flattenKeys(firstSubscriberContext);

    flattenedContextKeys.map((key) => {
      cols.push({
        Header: key,
        accessor: key,
        Cell: (props: RowProps) => {
          const value = getNestedValue(props.row.original?.context, key);
          if (!value) {
            return (
              <div className="flex h-full w-full items-center">
                <Badge type="secondary" flavour="warning">
                  {t('broadcasts.missing')}
                </Badge>
              </div>
            );
          }
          return <div>{value}</div>;
        },
      });
    });

    cols.push({
      Header: t('broadcasts.ingestion'),
      accessor: 'status',
      Cell: (props: RowProps) => {
        const row = props.row.original;
        let errorMessage = '';
        const isInvalidNumber = row.status_code === 'INVALID';
        const isMissingVariables = row.status_code === 'MISSING_VARIABLE';
        const isInvalidEmail = row.status_code === 'INVALID_EMAIL';

        if (isInvalidNumber) {
          errorMessage = t('contacts.errors.phone');
        }
        if (isMissingVariables) {
          errorMessage = t('broadcasts.missing_variables');
        }
        if (isInvalidEmail) {
          errorMessage = t('errors.invalid_email');
        }
        const hasError = isInvalidNumber || isMissingVariables || isInvalidEmail;
        return (
          <div className="flex h-full w-full items-center">
            <InfoTooltip content={errorMessage} delayDuration={300} showArrow>
              <span>
                <Badge type="secondary" flavour={hasError ? 'destructive' : 'successful'}>
                  {hasError ? t('status.failed') : t('status.success')}
                </Badge>
              </span>
            </InfoTooltip>
          </div>
        );
      },
    });

    return cols;
  }, [finalSubscribers, t]);

  const contextVariablesCount = getContextVariableOptions(finalSubscribers)?.length;

  const validSubscribersCount = isDraft
    ? validSubscribers.length
    : APIbroadcast?.subscribers_count + validSubscribers.length;

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/broadcasts/${broadcastId}`} className="breadcrumb-link">
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <ArrowLeftIcon size={16} className="text-foreground-muted" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        {t('main_navigation.broadcast_other')}
                      </div>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <button onClick={handleBackClick} className="breadcrumb-link">
                      {APIbroadcast?.name}
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t('broadcasts.audience')}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />
      <PageContentWrapper>
        <BroadcastLayout>
          <BroadcastLayout.Main>
            {isLoading && <Skeleton className="h-7 w-32" />}
            {finalSubscribers.length > 0 && (
              <PlainFieldset fullWidth overflown>
                <div ref={bodyRef}>
                  <Table
                    data={finalSubscribers}
                    columns={getColumns}
                    sortable
                    searchable={false}
                    filterable
                    noGutters
                  />
                </div>
                {hasNextPage && (
                  <div ref={loadMoreRef}>
                    <Skeleton className="h-10 w-full" />
                  </div>
                )}
              </PlainFieldset>
            )}
          </BroadcastLayout.Main>

          <BroadcastLayout.Sidebar>
            <SidebarBox>
              <SidebarFields
                validSubscribersCount={validSubscribersCount}
                errorsCount={errorCounter}
                contextVariablesCount={contextVariablesCount}
              />
            </SidebarBox>
          </BroadcastLayout.Sidebar>
        </BroadcastLayout>
      </PageContentWrapper>
    </>
  );
};
